import React, { useState, useEffect, useRef } from 'react';
import { BellIcon, XMarkIcon, ClockIcon, EnvelopeIcon, EnvelopeOpenIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { notificationApi } from '../services/notificationApi';
import { useMobile } from '../hooks/useMobile';
import { MobileBottomSheet } from './MobileBottomSheet';

interface Notification {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  data: any;
  read: boolean;
  createdAt: string;
  expiresAt?: string;
}

interface NotificationTrayProps {
  className?: string;
}

export const NotificationTray: React.FC<NotificationTrayProps> = ({ className = '' }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isMobile } = useMobile();

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      const [notificationsResponse, unreadCountResponse] = await Promise.all([
        notificationApi.getNotifications(1, 20),
        notificationApi.getUnreadCount()
      ]);
      
      setNotifications(notificationsResponse.notifications);
      setUnreadCount(unreadCountResponse);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const toggleReadStatus = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation();
    
    if (notification.read) {
      // Mark as unread - we need to implement this in the backend
      try {
        await notificationApi.markAsUnread(notification.id);
        setNotifications(prev => 
          prev.map(n => 
            n.id === notification.id 
              ? { ...n, read: false }
              : n
          )
        );
        setUnreadCount(prev => prev + 1);
      } catch (error) {
        console.error('Failed to mark notification as unread:', error);
      }
    } else {
      await markAsRead(notification.id);
    }
  };

  const clearAllNotifications = async () => {
    try {
      // Delete all notifications one by one since there's no bulk delete
      await Promise.all(notifications.map(n => notificationApi.deleteNotification(n.id)));
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      await notificationApi.deleteNotification(notificationId);
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
      // Update unread count if deleted notification was unread
      const deletedNotification = notifications.find(n => n.id === notificationId);
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const formatTimeAgo = (dateString: string | null | undefined): string => {
    try {
      if (!dateString) {
        console.warn('No date string provided to formatTimeAgo');
        return 'Unknown time';
      }

      let date: Date;

      // Handle various string formats
      if (dateString.includes('T') || dateString.includes('Z')) {
        // ISO string format
        date = new Date(dateString);
      } else if (dateString.match(/^\d+$/)) {
        // Unix timestamp
        date = new Date(parseInt(dateString));
      } else {
        // Try parsing as regular date string
        date = new Date(dateString);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date in notification:', dateString, 'Parsed as:', date);
        return 'Unknown time';
      }

      const now = new Date();
      const diffInMilliseconds = now.getTime() - date.getTime();

      // Handle future dates (clock skew, but allow small differences)
      if (diffInMilliseconds < -60000) { // More than 1 minute in the future
        console.warn('Notification date is in the future:', dateString, 'Diff:', diffInMilliseconds);
        return 'Just now';
      }

      const diffInMinutes = Math.floor(Math.abs(diffInMilliseconds) / 60000);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      const diffInWeeks = Math.floor(diffInDays / 7);
      const diffInMonths = Math.floor(diffInDays / 30);

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInHours < 24) return `${diffInHours}h ago`;
      if (diffInDays < 7) return `${diffInDays}d ago`;
      if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
      if (diffInMonths < 12) return `${diffInMonths}mo ago`;

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch (error) {
      console.error('Error formatting time ago:', error, 'Date string:', dateString);
      return 'Unknown time';
    }
  };

  const getNotificationIcon = (type: string) => {
    const iconClass = "w-5 h-5";
    
    switch (type) {
      case 'meetup_invitation':
      case 'meetup_invite':
      case 'meetup_location_selected':
      case 'meetup_location_cleared':
      case 'MEETUP_LOCATION_SELECTED':
        return <div className={`${iconClass} bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold`}>M</div>;
      case 'friend_request':
      case 'friend_request_accepted':
        return <div className={`${iconClass} bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold`}>F</div>;
      case 'group_invitation':
        return <div className={`${iconClass} bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold`}>G</div>;
      case 'guest_response':
      case 'guest_account_created':
        return <div className={`${iconClass} bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold`}>M</div>;
      case 'participant_joined':
      case 'participant_left':
        return <div className={`${iconClass} bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold`}>P</div>;
      default:
        return <div className={`${iconClass} bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-bold`}>•</div>;
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    switch (notification.type) {
      case 'friend_request':
        // Navigate to Friends page with requests tab active
        navigate('/friends#requests');
        setIsOpen(false);
        break;
      case 'friend_request_accepted':
        // Navigate to Friends page with friends list tab active
        navigate('/friends#friends');
        setIsOpen(false);
        break;
      case 'group_invitation':
        navigate('/friends#groups');
        setIsOpen(false);
        break;
      case 'meetup_invitation':
      case 'meetup_invite':
        // Navigate to invitations page for invitation notifications
        navigate('/invitations');
        setIsOpen(false);
        break;
      case 'meetup_update':
      case 'meetup_cancelled':
      case 'meetup_started':
      case 'meetup_ended':
      case 'meetup_reminder':
      case 'guest_response':
      case 'guest_account_created':
        // Check if meetup ID is in the data
        if (notification.data?.meetupId || notification.data?.relatedId) {
          navigate(`/meetups/${notification.data.meetupId || notification.data.relatedId}`);
        } else {
          navigate('/meetups');
        }
        setIsOpen(false);
        break;
      case 'participant_joined':
      case 'participant_left':
      case 'meetup_location_selected':
      case 'meetup_location_cleared':
      case 'MEETUP_LOCATION_SELECTED':
        if (notification.data?.meetupId) {
          navigate(`/meetups/${notification.data.meetupId}`);
        } else {
          navigate('/meetups');
        }
        setIsOpen(false);
        break;
      default:
        // For unknown types, just mark as read
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch notifications initially and set up polling
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    
    // Listen for invitation response events to refresh notifications immediately
    const handleInvitationResponse = () => {
      fetchNotifications();
    };
    
    window.addEventListener('invitation-responded', handleInvitationResponse);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('invitation-responded', handleInvitationResponse);
    };
  }, []);

  // Shared notification list content
  const notificationContent = (
    <>
      {/* Header Actions */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-primary dark:text-blue-400 hover:opacity-80 font-medium min-h-[44px] px-2"
              >
                Mark all read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={clearAllNotifications}
                className="text-sm text-red-600 dark:text-red-400 hover:opacity-80 font-medium flex items-center gap-1 min-h-[44px] px-2"
              >
                <TrashIcon className="w-4 h-4" />
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="px-4 py-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary dark:border-blue-400 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <BellIcon className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {notifications.slice(0, 20).map((notification) => (
              <div
                key={notification.id}
                className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer min-h-[60px] ${
                  !notification.read ? 'bg-gray-50 dark:bg-gray-800/50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(notification); } }}
                role="button"
                tabIndex={0}
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                          {notification.title}
                        </p>
                        <p className={`text-sm mt-1 ${!notification.read ? 'text-gray-800 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>
                          {notification.message}
                        </p>
                        <div className="flex items-center mt-1">
                          <ClockIcon className="w-3 h-3 text-gray-400 dark:text-gray-500 mr-1" />
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {formatTimeAgo(notification.createdAt)}
                          </p>
                          {!notification.read && (
                            <div className="ml-2 w-2 h-2 bg-primary dark:bg-blue-400 rounded-full"></div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={(e) => toggleReadStatus(e, notification)}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                          title={notification.read ? 'Mark as unread' : 'Mark as read'}
                        >
                          {notification.read ? (
                            <EnvelopeOpenIcon className="w-4 h-4" />
                          ) : (
                            <EnvelopeIcon className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                          title="Delete notification"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-xs text-gray-500 dark:text-gray-400">
            Showing {Math.min(20, notifications.length)} of {notifications.length} notifications
          </p>
        </div>
      )}
    </>
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-white/90 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/30 transition-all duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center"
      >
        <span className="sr-only">View notifications</span>
        <BellIcon className="h-6 w-6" aria-hidden="true" />
        
        {/* Unread count badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Mobile: Bottom Sheet */}
      {isMobile && (
        <MobileBottomSheet
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title="Notifications"
          initialSnap={1}
        >
          {notificationContent}
        </MobileBottomSheet>
      )}

      {/* Desktop: Dropdown */}
      {!isMobile && (
        <Transition
          show={isOpen}
          enter="transition ease-out duration-200"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-900 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none z-50">
            {notificationContent}
          </div>
        </Transition>
      )}
    </div>
  );
};
